import { useCallback, useState } from "react";
import { useI18n, useMoneyFormatter } from "@expertloop/i18n/react";
import type { AnalyticsPeriodKey, ExpertReferralIndicators } from "@expertloop/shared";
import { expertReferralsApi } from "../features/expert-referrals/api";
import { useAdminSection } from "../features/admin-sections/useAdminSection";
import { SectionShell } from "../features/admin-sections/SectionShell";
import { PeriodPicker } from "../features/admin-sections/PeriodPicker";
import { FunnelSection } from "../features/overview/FunnelSection";
import "./OverviewPage.css";
import "./FinancePage.css";

function Tile({ label, value }: { label: string; value: string }) {
	return (
		<div className="admin-overview__tile">
			<span className="admin-overview__tile-label">{label}</span>
			<strong className="admin-overview__tile-value">{value}</strong>
		</div>
	);
}

/**
 * Parrainage entre experts (lot E6).
 *
 * La page suit la cohorte des experts parrainés pendant la fenêtre : ce qu'ils
 * sont devenus, ce que leurs parrains ont gagné et ce qu'ils ont rapporté. Le
 * coût réel se lit dans le registre des récompenses, jamais dans Stripe.
 */
export function ExpertReferralsPage() {
	const { t } = useI18n();
	const [period, setPeriod] = useState<AnalyticsPeriodKey>("30d");
	const [referrerId, setReferrerId] = useState<string | null>(null);
	// Les parrains proposés au filtre viennent de la dernière lecture sans filtre
	const [referrers, setReferrers] = useState<{ id: string; name: string }[]>([]);

	const section = useAdminSection(
		useCallback(async () => {
			const result = await expertReferralsApi.indicators(period, referrerId);
			if (result.enabled && !referrerId) {
				setReferrers(result.indicators.topReferrers.map((row) => ({ id: row.referrerId, name: row.displayName })));
			}
			return result;
		}, [period, referrerId]),
		[period, referrerId],
	);

	return (
		<div className="admin-overview admin-finance">
			<div className="page-header">
				<h1>{t("adminExpertReferral.title")}</h1>
				<p>{t("adminExpertReferral.subtitle")}</p>
			</div>

			<PeriodPicker value={period} onChange={setPeriod} />

			<SectionShell section={section}>
				{(result) => result.enabled
					? (
						<Indicators
							indicators={result.indicators}
							referrers={referrers}
							referrerId={referrerId}
							onReferrerChange={setReferrerId}
						/>
					)
					: <p className="admin-finance__scope">{t("adminExpertReferral.disabled")}</p>}
			</SectionShell>
		</div>
	);
}

interface IndicatorsProps {
	indicators: ExpertReferralIndicators;
	referrers: { id: string; name: string }[];
	referrerId: string | null;
	onReferrerChange: (referrerId: string | null) => void;
}

function Indicators({ indicators, referrers, referrerId, onReferrerChange }: IndicatorsProps) {
	const { t } = useI18n();
	const { formatCents } = useMoneyFormatter();
	const { cohort } = indicators;

	const rate = indicators.attachmentsPerPublishedExpertPerMonth;
	const funnel = {
		steps: [
			{ id: "referralAttached", count: cohort.attached },
			{ id: "referralStripeReady", count: cohort.stripeReady },
			{ id: "referralPublished", count: cohort.published },
			{ id: "referralFirstSale", count: cohort.firstSale },
			{ id: "referralSubscribed", count: cohort.subscribed },
		],
		limitations: indicators.limitations,
	};

	return (
		<>
			<div className="admin-finance__filter">
				<label htmlFor="referral-referrer">{t("adminExpertReferral.referrerFilter")}</label>
				<select
					id="referral-referrer"
					value={referrerId ?? ""}
					onChange={(event) => onReferrerChange(event.target.value || null)}
				>
					<option value="">{t("adminExpertReferral.allReferrers")}</option>
					{referrers.map((referrer) => (
						<option key={referrer.id} value={referrer.id}>{referrer.name}</option>
					))}
				</select>
			</div>

			<section className="admin-overview__section">
				<h2>{t("adminExpertReferral.cohortTitle")}</h2>
				<div className="admin-overview__tiles">
					<Tile label={t("adminExpertReferral.activeReferrers")} value={String(indicators.activeReferrers)} />
					<Tile label={t("adminExpertReferral.publishedExperts")} value={String(indicators.publishedExperts)} />
					<Tile
						label={t("adminExpertReferral.attachmentRate")}
						value={rate === null ? t("adminExpertReferral.notAvailable") : rate.toFixed(2)}
					/>
				</div>
				<div className="admin-overview__funnels">
					<FunnelSection title={t("adminOverview.expertReferralFunnel")} funnel={funnel} />
				</div>
			</section>

			<section className="admin-overview__section">
				<h2>{t("adminExpertReferral.rewardsTitle")}</h2>
				<div className="admin-overview__tiles">
					<Tile label={t("adminExpertReferral.proMonths")} value={String(indicators.rewardProMonths)} />
				</div>
				{indicators.byCurrency.length === 0
					? <p className="admin-overview__state">{t("adminExpertReferral.noRewards")}</p>
					: (
						<div className="admin-overview__currencies">
							{indicators.byCurrency.map((row) => (
								<div key={row.currency} className="admin-overview__currency">
									<h4 className="admin-overview__currency-title">{row.currency.toUpperCase()}</h4>
									<div className="admin-overview__tiles">
										<Tile label={t("adminExpertReferral.rewardFaceValue")} value={formatCents(row.rewardFaceValue, row.currency)} />
										<Tile label={t("adminExpertReferral.rewardLostRevenue")} value={formatCents(row.rewardLostRevenue, row.currency)} />
										<Tile label={t("adminExpertReferral.refereeRevenue")} value={formatCents(row.refereeRevenue, row.currency)} />
										<Tile
											label={t("adminExpertReferral.costRevenueRatio")}
											value={row.costRevenueRatio === null ? t("adminExpertReferral.notAvailable") : row.costRevenueRatio.toFixed(2)}
										/>
									</div>
								</div>
							))}
						</div>
					)}
			</section>

			<section className="admin-overview__section">
				<h2>{t("adminExpertReferral.topReferrersTitle")}</h2>
				{indicators.topReferrers.length === 0
					? <p className="admin-overview__state">{t("adminExpertReferral.noReferrals")}</p>
					: (
						<div className="table-container">
							<table>
								<thead>
									<tr>
										<th>{t("adminExpertReferral.columnReferrer")}</th>
										<th>{t("adminExpertReferral.columnAttached")}</th>
										<th>{t("adminExpertReferral.columnPublished")}</th>
										<th>{t("adminExpertReferral.columnFirstSale")}</th>
										<th>{t("adminExpertReferral.columnSubscribed")}</th>
									</tr>
								</thead>
								<tbody>
									{indicators.topReferrers.map((row) => (
										<tr key={row.referrerId}>
											<td>
												<button
													type="button"
													className="admin-overview__retry"
													aria-label={t("adminExpertReferral.filterBy", { name: row.displayName })}
													onClick={() => onReferrerChange(row.referrerId)}
												>
													{row.displayName}
												</button>
											</td>
											<td>{row.attached}</td>
											<td>{row.published}</td>
											<td>{row.firstSale}</td>
											<td>{row.subscribed}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
			</section>

			<section className="admin-overview__section">
				<h2>{t("adminExpertReferral.alertsTitle")}</h2>
				<CostAlerts indicators={indicators} />
			</section>

			<section className="admin-overview__section">
				<h2>{t("adminExpertReferral.applicationTitle")}</h2>
				<p className="admin-overview__state">
					{t(indicators.application.dryRun ? "adminExpertReferral.applicationDryRun" : "adminExpertReferral.applicationLive")}
				</p>
				<div className="admin-overview__tiles">
					{indicators.application.dryRun
						? (
							<>
								<Tile label={t("adminExpertReferral.plannedCredit")} value={formatCents(indicators.application.plannedCreditMinor, "EUR")} />
								<Tile label={t("adminExpertReferral.plannedProMonths")} value={String(indicators.application.plannedProMonths)} />
							</>
						)
						: (
							<>
								<Tile label={t("adminExpertReferral.appliedCredit")} value={formatCents(indicators.application.appliedCreditMinor, "EUR")} />
								<Tile label={t("adminExpertReferral.appliedProMonths")} value={String(indicators.application.appliedProMonths)} />
							</>
						)}
					<Tile label={t("adminExpertReferral.discrepancies")} value={String(indicators.application.discrepancies)} />
				</div>
			</section>

			<section className="admin-overview__section">
				<h2>{t("adminExpertReferral.retentionTitle")}</h2>
				<p className="admin-overview__state">
					{t(indicators.retention.purgeEnabled ? "adminExpertReferral.retentionEnabled" : "adminExpertReferral.retentionDryRun")}
				</p>
				<div className="admin-overview__tiles">
					<Tile label={t("adminExpertReferral.retentionReferrals")} value={String(indicators.retention.referralsDue)} />
					<Tile label={t("adminExpertReferral.retentionJournal")} value={String(indicators.retention.journalEntriesDue)} />
					<Tile label={t("adminExpertReferral.retentionInvitationJournal")} value={String(indicators.retention.invitationJournalEntriesDue)} />
				</div>
			</section>
		</>
	);
}

function CostAlerts({ indicators }: { indicators: ExpertReferralIndicators }) {
	const { t } = useI18n();
	if (indicators.alertThreshold === null) {
		return <p className="admin-overview__state admin-overview__state--muted">{t("adminExpertReferral.alertsOff")}</p>;
	}
	return (
		<>
			<p className="admin-overview__state">
				{t("adminExpertReferral.alertsThreshold", { threshold: indicators.alertThreshold })}
			</p>
			{indicators.alerts.length === 0
				? <p className="admin-overview__state">{t("adminExpertReferral.alertsNone")}</p>
				: (
					<ul>
						{indicators.alerts.map((alert) => (
							<li key={`${alert.referralId}:${alert.currency}`}>
								{alert.refereeName} · {alert.currency.toUpperCase()} ·{" "}
								{alert.costRevenueRatio === null ? t("adminExpertReferral.alertNoRevenue") : alert.costRevenueRatio.toFixed(2)}
							</li>
						))}
					</ul>
				)}
		</>
	);
}
